import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ConnectionProvider, WalletProvider as SolanaWalletProvider, useWallet as useSolanaWallet } from '@solana/wallet-adapter-react';
import { WalletAdapterNetwork } from '@solana/wallet-adapter-base';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import { WalletModalProvider, WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { clusterApiUrl } from '@solana/web3.js';

// Import wallet adapter CSS
import '@solana/wallet-adapter-react-ui/styles.css';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

// Internal wallet context that uses Solana wallet adapter
const InternalWalletProvider = ({ children }) => {
  const solanaWallet = useSolanaWallet();
  const { wallet, publicKey, connected, disconnect, connecting, sendTransaction, signTransaction, signAllTransactions } = solanaWallet;
  const [balance, setBalance] = useState(0);
  const [usdPrice, setUsdPrice] = useState(0);

  // Fetch SOL balance and USD price
  useEffect(() => {
    if (publicKey) {
      // Store wallet address in localStorage for hooks to access
      localStorage.setItem('connectedWallet', publicKey.toString());
      
      const fetchBalance = async () => {
        try {
          // Use Helius mainnet RPC endpoint for better reliability
          const heliusKey = import.meta.env.VITE_HELIUS_API_KEY || import.meta.env.HELIUS_API_KEY;
          const rpcEndpoint = heliusKey 
            ? `https://mainnet.helius-rpc.com/?api-key=${heliusKey}`
            : 'https://api.mainnet-beta.solana.com';
          
          // Debug: Log which endpoint we're using
          console.log('🔍 RPC Debug:', {
            hasHeliusKey: !!heliusKey,
            endpoint: heliusKey ? 'Helius mainnet' : 'Public RPC (fallback)',
            fullEndpoint: rpcEndpoint.replace(/api-key=.+/, 'api-key=***')
          });
          
          const connection = new (await import('@solana/web3.js')).Connection(
            rpcEndpoint,
            'confirmed'
          );
          
          const balance = await connection.getBalance(publicKey);
          const solBalance = balance / 1e9; // Convert lamports to SOL
          setBalance(solBalance);

          // Fetch SOL to USD price with multiple fallbacks
          try {
            // Try CoinGecko first (most reliable, works globally)
            try {
              const cgResponse = await fetch(
                'https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd',
                { 
                  headers: { 'Accept': 'application/json' },
                  signal: AbortSignal.timeout(8000) // 8 second timeout
                }
              );
              
              if (cgResponse.ok) {
                const cgData = await cgResponse.json();
                if (cgData.solana?.usd) {
                  setUsdPrice(cgData.solana.usd);
                  return;
                }
              }
            } catch (cgError) {
              console.warn('CoinGecko price fetch failed, trying fallback...');
            }
            
            // Fallback 1: CryptoCompare
            try {
              const response = await fetch(
                'https://min-api.cryptocompare.com/data/price?fsym=SOL&tsyms=USD',
                { 
                  headers: { 'Accept': 'application/json' },
                  signal: AbortSignal.timeout(8000)
                }
              );
              
              if (response.ok) {
                const data = await response.json();
                if (data.USD) {
                  setUsdPrice(data.USD);
                  return;
                }
              }
            } catch (ccError) {
              console.warn('CryptoCompare price fetch failed, trying fallback...');
            }
            
            // Fallback 2: Coinbase public API
            try {
              const response = await fetch(
                'https://api.coinbase.com/v2/prices/SOL-USD/spot',
                { 
                  headers: { 'Accept': 'application/json' },
                  signal: AbortSignal.timeout(8000)
                }
              );
              
              if (response.ok) {
                const data = await response.json();
                if (data.data?.amount) {
                  setUsdPrice(parseFloat(data.data.amount));
                  return;
                }
              }
            } catch (cbError) {
              console.warn('Coinbase price fetch failed, trying fallback...');
            }
            
            // Fallback 3: Kraken public API
            try {
              const response = await fetch(
                'https://api.kraken.com/0/public/Ticker?pair=SOLUSD',
                { 
                  headers: { 'Accept': 'application/json' },
                  signal: AbortSignal.timeout(8000)
                }
              );
              
              if (response.ok) {
                const data = await response.json();
                if (data.result?.SOLUSD?.c?.[0]) {
                  setUsdPrice(parseFloat(data.result.SOLUSD.c[0]));
                  return;
                }
              }
            } catch (krakenError) {
              console.warn('Kraken price fetch failed');
            }
            
            // Use a reasonable default price if all APIs fail
            console.warn('All price sources failed, using default price of $150');
            setUsdPrice(150); // Reasonable default for SOL
          } catch (priceError) {
            console.warn('Price fetch failed:', priceError.message);
            setUsdPrice(150); // Fallback to default
          }
        } catch (error) {
          // Only log error if it's not a 403 (rate limit) error
          if (!error.message?.includes('403')) {
            console.error('Error fetching balance:', error);
          }
        }
      };

      fetchBalance();
    } else {
      // Clear wallet address from localStorage when disconnected
      localStorage.removeItem('connectedWallet');
      setBalance(0);
      setUsdPrice(0);
    }
  }, [publicKey]);

  const connectWallet = async () => {
    // This will be handled by the wallet adapter
    console.log('Connect wallet triggered');
  };

  const disconnectWallet = async () => {
    if (disconnect) {
      await disconnect();
    }
  };

  const value = {
    wallet: {
      ...wallet,
      publicKey,
      connected,
      sendTransaction,
      signTransaction,
      signAllTransactions
    },
    publicKey,
    balance,
    usdPrice,
    isConnected: connected,
    isConnecting: connecting,
    connectWallet,
    disconnectWallet
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export const WalletProvider = ({ children }) => {
  // The network can be set to 'devnet', 'testnet', or 'mainnet-beta'
  // Using mainnet-beta for production
  const network = WalletAdapterNetwork.Mainnet;

  // You can also provide a custom RPC endpoint
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);

  // App identity metadata for wallet trust
  // This ensures Phantom and other wallets recognize the dApp as legitimate
  // and don't show security warnings to users in production
  // CRITICAL: These values must match the production domain for wallet trust
  const walletAppIdentity = {
    name: import.meta.env.VITE_APP_NAME || 'Outsera',
    url: import.meta.env.VITE_APP_URL || 'https://outsera.io',
    icon: import.meta.env.VITE_APP_ICON || 'https://outsera.io/images/logo.png'
  };

  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(walletAppIdentity),
      new SolflareWalletAdapter(walletAppIdentity),
    ],
    []
  );

  return (
    <ConnectionProvider endpoint={endpoint}>
      <SolanaWalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>
          <InternalWalletProvider>
            {children}
          </InternalWalletProvider>
        </WalletModalProvider>
      </SolanaWalletProvider>
    </ConnectionProvider>
  );
}; 